import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { WorkflowTemplateDynamicField } from './WorkflowTemplateDynamicField';
import { WorkflowVariableAutocompleteInput } from './WorkflowVariableAutocompleteInput';
import type {
  Option,
  WorkflowActionSchema,
  WorkflowDecisionSchema,
} from './WorkflowTemplateBuilder.types';

type WorkflowTemplateStepCardProps = {
  field: any;
  index: number;
  remove: (index: number | number[]) => void;
  stepOptions: Option[];
  workflowActionOptions: Option[];
  workflowDecisionOptions: Option[];
  workflowActionSchemas: Record<string, WorkflowActionSchema>;
  workflowDecisionSchemas: Record<string, WorkflowDecisionSchema>;
  conditionOptions: Option[];
};

export const WorkflowTemplateStepCard = ({
  field,
  index,
  remove,
  stepOptions,
  workflowActionOptions,
  workflowDecisionOptions,
  workflowActionSchemas,
  workflowDecisionSchemas,
  conditionOptions,
}: WorkflowTemplateStepCardProps) => {
  const requiredInputs =
    (Form.useWatch(['workflow', 'required_inputs']) as string[] | undefined) ||
    [];
  const variableOptions = requiredInputs
    .filter((value) => Boolean(String(value || '').trim()))
    .map((value) => ({ value: `$.${String(value).trim()}` }));

  const rowLabel = (label: string, required = false) => (
    <span className="text-xs text-[#595959]">
      {required ? <span className="text-[#ff4d4f] mr-1">*</span> : null}
      {label}
    </span>
  );

  return (
    <Card
      key={field.key}
      size="small"
      title={`Step ${index + 1}`}
      className="[&_.ant-form-item]:!mb-1.5 [&_.ant-form-item-explain]:!text-[11px] [&_.ant-card-head]:!min-h-[34px] [&_.ant-card-head-title]:!text-sm [&_.ant-card-head-title]:!py-1 [&_.ant-typography]:!text-xs"
      extra={
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => remove(field.name)}
        />
      }
    >
      <div className="grid grid-cols-1 gap-1">
        <div className="flex items-center gap-2">
          <div className="w-24 flex-none">{rowLabel('Step ID', true)}</div>
          <Form.Item
            name={[field.name, 'id']}
            rules={[{ required: true, message: 'Step ID is required' }]}
            className="!mb-0 flex-1"
          >
            <Input size="small" placeholder="step_1" />
          </Form.Item>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-24 flex-none">{rowLabel('Name', true)}</div>
          <Form.Item
            name={[field.name, 'name']}
            rules={[{ required: true, message: 'Step name is required' }]}
            className="!mb-0 flex-1"
          >
            <Input size="small" placeholder="Validate request" />
          </Form.Item>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-24 flex-none">{rowLabel('Type', true)}</div>
          <Form.Item
            name={[field.name, 'type']}
            rules={[{ required: true, message: 'Step type is required' }]}
            className="!mb-0 flex-1"
          >
            <Select
              size="small"
              options={[
                { label: 'action', value: 'action' },
                { label: 'decision', value: 'decision' },
              ]}
            />
          </Form.Item>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-24 flex-none">{rowLabel('Depends On')}</div>
          <Form.Item name={[field.name, 'depends_on']} className="!mb-0 flex-1">
            <Select
              size="small"
              mode="multiple"
              options={stepOptions}
              placeholder="Select dependencies"
            />
          </Form.Item>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-24 flex-none">{rowLabel('Retry Max')}</div>
          <Form.Item
            name={[field.name, 'retry_max_attempts']}
            className="!mb-0 flex-1"
          >
            <InputNumber size="small" className="w-full" min={0} />
          </Form.Item>
        </div>

        <div className="flex items-center gap-2">
          <div className="w-24 flex-none">{rowLabel('Continue')}</div>
          <Form.Item
            name={[field.name, 'continue_on_failure']}
            valuePropName="checked"
            className="!mb-0 flex-1"
          >
            <Switch />
          </Form.Item>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-1">
        <div className="w-24 flex-none">{rowLabel('Description')}</div>
        <Form.Item name={[field.name, 'description']} className="!mb-0 flex-1">
          <WorkflowVariableAutocompleteInput
            textArea
            rows={2}
            workflowActionSchemas={workflowActionSchemas}
            workflowDecisionSchemas={workflowDecisionSchemas}
            showLegend
          />
        </Form.Item>
      </div>

      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const stepType = getFieldValue([
            'workflow',
            'steps',
            field.name,
            'type',
          ]);

          if (stepType === 'decision') {
            const decisionType = getFieldValue([
              'workflow',
              'steps',
              field.name,
              'decision',
              'type',
            ]);
            const decisionSchema = workflowDecisionSchemas[decisionType] || {};
            const decisionInputs = decisionSchema.inputs || {};

            return (
              <Card
                size="small"
                title="Decision Configuration"
                className="[&_.ant-form-item]:!mb-1.5 [&_.ant-form-item-row]:!flex [&_.ant-form-item-row]:!items-center [&_.ant-form-item-label]:!w-24 [&_.ant-form-item-label]:!flex-none [&_.ant-form-item-control]:!flex-1 [&_.ant-card-head]:!min-h-[32px] [&_.ant-card-head-title]:!py-1"
              >
                <Form.Item
                  name={[field.name, 'decision', 'type']}
                  className="!mb-0"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-24 flex-none">{rowLabel('Decision')}</div>
                    <div className="flex-1">
                      <Select
                        size="small"
                        options={workflowDecisionOptions}
                        showSearch
                        allowClear
                      />
                    </div>
                  </div>
                </Form.Item>

                <div className="grid grid-cols-1 gap-2">
                  {Object.entries(decisionInputs).map(
                    ([inputName, inputSchema]) => (
                      <WorkflowTemplateDynamicField
                        key={`decision-${field.key}-${inputName}`}
                        fieldPath={[field.name, 'decision']}
                        inputName={inputName}
                        inputSchema={inputSchema}
                        variableOptions={variableOptions}
                        workflowActionSchemas={workflowActionSchemas}
                        workflowDecisionSchemas={workflowDecisionSchemas}
                      />
                    )
                  )}
                </div>
              </Card>
            );
          }

          const actionType = getFieldValue([
            'workflow',
            'steps',
            field.name,
            'action',
            'type',
          ]);
          const actionSchema = workflowActionSchemas[actionType] || {};
          const actionInputs = actionSchema.inputs || {};

          return (
            <Card
              size="small"
              title="Action Configuration"
              className="[&_.ant-form-item]:!mb-1.5 [&_.ant-form-item-row]:!flex [&_.ant-form-item-row]:!items-center [&_.ant-form-item-label]:!w-24 [&_.ant-form-item-label]:!flex-none [&_.ant-form-item-control]:!flex-1 [&_.ant-card-head]:!min-h-[32px] [&_.ant-card-head-title]:!py-1"
            >
              <Form.Item
                name={[field.name, 'action', 'type']}
                className="!mb-0"
              >
                <div className="flex items-center gap-2">
                  <div className="w-24 flex-none">{rowLabel('Action')}</div>
                  <div className="flex-1">
                    <Select
                      size="small"
                      options={workflowActionOptions}
                      showSearch
                      allowClear
                    />
                  </div>
                </div>
              </Form.Item>

              <div className="grid grid-cols-1 gap-2">
                {Object.entries(actionInputs).map(
                  ([inputName, inputSchema]) => (
                    <WorkflowTemplateDynamicField
                      key={`action-${field.key}-${inputName}`}
                      fieldPath={[field.name, 'action', 'inputs']}
                      inputName={inputName}
                      inputSchema={inputSchema}
                      variableOptions={variableOptions}
                      workflowActionSchemas={workflowActionSchemas}
                      workflowDecisionSchemas={workflowDecisionSchemas}
                    />
                  )
                )}
              </div>
            </Card>
          );
        }}
      </Form.Item>

      <Card
        size="small"
        title="Routing"
        className="mt-2 [&_.ant-form-item]:!mb-1.5 [&_.ant-form-item-row]:!flex [&_.ant-form-item-row]:!items-center [&_.ant-form-item-label]:!w-24 [&_.ant-form-item-label]:!flex-none [&_.ant-form-item-control]:!flex-1 [&_.ant-card-head]:!min-h-[32px] [&_.ant-card-head-title]:!py-1"
      >
        <div className="flex items-center gap-2">
          <div className="w-24 flex-none">{rowLabel('Default Next')}</div>
          <Form.Item
            name={[field.name, 'next', 'default']}
            className="!mb-0 flex-1"
          >
            <Select
              size="small"
              options={[{ label: 'end', value: 'end' }, ...stepOptions]}
              showSearch
              allowClear
            />
          </Form.Item>
        </div>

        <Form.List name={[field.name, 'next', 'conditions']}>
          {(
            conditionFields,
            { add: addCondition, remove: removeCondition }
          ) => (
            <div className="space-y-2">
              {conditionFields.map((conditionField) => (
                <Space
                  key={conditionField.key}
                  align="start"
                  className="w-full"
                  wrap
                >
                  <Form.Item
                    label="Field"
                    name={[conditionField.name, 'when', 'field']}
                  >
                    <Input size="small" placeholder="status" />
                  </Form.Item>
                  <Form.Item
                    label="Operator"
                    name={[conditionField.name, 'when', 'operator']}
                  >
                    <Select
                      size="small"
                      className="min-w-[180px]"
                      options={conditionOptions}
                      showSearch
                    />
                  </Form.Item>
                  <Form.Item
                    label="Value"
                    name={[conditionField.name, 'when', 'value']}
                  >
                    <WorkflowVariableAutocompleteInput
                      size="small"
                      placeholder="approved"
                      additionalOptions={variableOptions.map(
                        (opt) => opt.value
                      )}
                      workflowActionSchemas={workflowActionSchemas}
                      workflowDecisionSchemas={workflowDecisionSchemas}
                      showLegend
                    />
                  </Form.Item>
                  <Form.Item label="Then" name={[conditionField.name, 'then']}>
                    <Select
                      size="small"
                      className="min-w-[160px]"
                      options={[{ label: 'end', value: 'end' }, ...stepOptions]}
                      showSearch
                    />
                  </Form.Item>
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => removeCondition(conditionField.name)}
                  />
                </Space>
              ))}
              <Button
                icon={<PlusOutlined />}
                onClick={() =>
                  addCondition({
                    when: {
                      field: '',
                      operator: undefined,
                      value: '',
                    },
                    then: undefined,
                  })
                }
              >
                Add Condition
              </Button>
            </div>
          )}
        </Form.List>
      </Card>
    </Card>
  );
};
